import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Radar, TrendingUp, FileText, Gauge, Award } from "lucide-react";
import { Button } from "./ui/button";

// Score dial with animated arc
function ScoreDial({ score = 62, label = "Cyber Score" }) {
  const r = 70;
  const c = 2 * Math.PI * r;
  const dash = (score / 100) * c;
  return (
    <div className="relative w-44 h-44 mx-auto">
      <svg viewBox="0 0 180 180" className="w-full h-full -rotate-90">
        <defs>
          <linearGradient id="scoreGrad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#38BDF8" />
            <stop offset="60%" stopColor="#F97316" />
            <stop offset="100%" stopColor="#F97316" />
          </linearGradient>
        </defs>
        <circle cx="90" cy="90" r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="14" />
        <motion.circle
          cx="90"
          cy="90"
          r={r}
          fill="none"
          stroke="url(#scoreGrad)"
          strokeWidth="14"
          strokeLinecap="round"
          initial={{ strokeDasharray: `0 ${c}` }}
          whileInView={{ strokeDasharray: `${dash} ${c}` }}
          viewport={{ once: true }}
          transition={{ duration: 1.6, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center rotate-0">
        <div className="text-center">
          <div className="font-display text-4xl font-extrabold text-white">{score}</div>
          <div className="text-[10px] font-bold uppercase tracking-widest text-white/70">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default function CyberRiskCTA() {
  return (
    <section id="cyber-risk-navigator" className="py-20 sm:py-28 relative overflow-hidden">
      <div className="absolute inset-0 -z-0 grid-pattern opacity-30 dark:opacity-20" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="rounded-[2rem] border border-border bg-gradient-to-br from-primary via-primary to-[#0a3560] text-primary-foreground p-8 sm:p-12 lg:p-16 relative overflow-hidden"
          data-testid="cyber-risk-cta-card"
        >
          <div className="absolute inset-0 opacity-25 pointer-events-none">
            <div className="absolute top-0 right-0 h-96 w-96 rounded-full bg-secondary/40 blur-3xl" />
            <div className="absolute bottom-0 left-0 h-80 w-80 rounded-full bg-accent/40 blur-3xl" />
          </div>
          <div className="grid lg:grid-cols-12 gap-10 items-center relative">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1.5 text-xs font-bold uppercase tracking-widest">
                <ShieldCheck className="h-3.5 w-3.5" /> Cyber Risk Navigator™
              </span>
              <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight leading-[1.02]">
                Get your <span className="text-secondary">Cyber Score</span> in minutes.
              </h2>
              <p className="mt-5 text-white/85 text-base sm:text-lg leading-relaxed max-w-xl">
                A free, executive cybersecurity assessment prepared by senior consultants at Techmate Infosec Solutions.
                Answer a short, business-focused survey — receive a personalized Cybersecurity Readiness Report,
                your Cyber Score, a compliance radar and a phased roadmap tailored to your industry.
              </p>
              <div className="mt-6 grid grid-cols-3 gap-3 max-w-md">
                <BenefitStat icon={Gauge} label="Cyber Score" val="0–100" />
                <BenefitStat icon={Radar} label="Compliance Radar" val="8 frameworks" />
                <BenefitStat icon={FileText} label="Executive Report" val="1-page PDF" />
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button asChild size="lg" className="rounded-full bg-secondary hover:bg-secondary/90 text-secondary-foreground h-12 px-6 shadow-xl shadow-secondary/25">
                  <Link data-testid="cta-start-assessment-btn" to="/cyber-risk-navigator">
                    Start Free Assessment <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" size="lg" className="rounded-full h-12 px-6 bg-transparent border-white/30 text-white hover:bg-white/10 hover:text-white">
                  <Link data-testid="cta-learn-more-btn" to="/cyber-risk-navigator#learn">How it works</Link>
                </Button>
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="relative">
                {/* Report Preview Card */}
                <div className="rounded-3xl bg-white/[0.08] backdrop-blur-xl border border-white/20 p-5 shadow-2xl">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-white/85">
                      <Award className="h-3.5 w-3.5" /> Sample Cyber Score Report
                    </div>
                    <div className="text-[10px] font-semibold text-white/60">Preview</div>
                  </div>
                  <div className="mt-3 grid grid-cols-5 gap-3">
                    <div className="col-span-3">
                      <ScoreDial score={62} />
                    </div>
                    <div className="col-span-2 space-y-2">
                      {[
                        {k:"Risk", v:"Moderate", c:"bg-secondary"},
                        {k:"Peers", v:"P58", c:"bg-accent"},
                        {k:"Trend", v:"↑ 12%", c:"bg-white/25"},
                      ].map((c) => (
                        <div key={c.k} className="rounded-xl bg-white/5 border border-white/10 p-2.5">
                          <div className="text-[9px] uppercase tracking-widest text-white/60">{c.k}</div>
                          <div className="mt-0.5 font-display font-bold text-sm">{c.v}</div>
                          <div className={`mt-1.5 h-1 rounded-full ${c.c}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                      <div className="text-[9px] uppercase tracking-widest text-white/60 flex items-center gap-1"><Radar className="h-3 w-3" /> Compliance Radar</div>
                      <svg viewBox="0 0 100 100" className="mt-2 w-full">
                        <polygon points="50,10 85,30 80,72 20,72 15,30" fill="none" stroke="rgba(255,255,255,0.3)" />
                        <polygon points="50,25 78,38 72,66 28,66 22,38" fill="none" stroke="rgba(255,255,255,0.3)" />
                        <polygon points="50,20 74,40 60,64 40,64 26,40" fill="#F97316" fillOpacity="0.55" stroke="#F97316" />
                      </svg>
                    </div>
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                      <div className="text-[9px] uppercase tracking-widest text-white/60 flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Readiness Trend</div>
                      <svg viewBox="0 0 100 60" className="mt-2 w-full">
                        <polyline points="0,50 15,42 30,45 45,32 60,28 75,18 100,12" fill="none" stroke="#38BDF8" strokeWidth="2.5" />
                        <polyline points="0,50 15,42 30,45 45,32 60,28 75,18 100,12" fill="none" stroke="#F97316" strokeWidth="1" strokeDasharray="2 3" opacity="0.7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function BenefitStat({ icon: Icon, label, val }) {
  return (
    <div className="rounded-xl bg-white/10 border border-white/15 px-3 py-2.5">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/70">
        <Icon className="h-3 w-3" /> {label}
      </div>
      <div className="mt-1 font-display text-sm font-bold">{val}</div>
    </div>
  );
}
